import { Heading, IconButton } from '@assembly-js/design-system';
import { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ImmersiveModalWrapper } from 'src/components/dialogs/ImmersiveModalWrapper';
import { ResponsiveTabs } from 'src/components/ResponsiveTabs';
import {
  AssistantProvider,
  DRAWER_IDS,
  DRAWER_LABELS,
  DrawerId,
  MOBILE_DRAWER_LABELS,
} from 'src/context';
import { ContextBar } from 'src/features/context-bar/ContextBar';
import { ContextBarAvatar } from 'src/features/context-bar/ContextBarAvatar';
import {
  ContextBarNavTabs,
  NavTab,
} from 'src/features/context-bar/ContextBarNavTabs';
import { getEntityName, useEntity } from 'src/features/context-bar/utils';
import { NoteModalProvider } from 'src/features/notes/NoteModal/noteModalContext';
import { ResizableGroup } from 'src/features/resizableWrapper/ResizableGroup';
import {
  getRightPanelSizeProps,
  ResizablePanel,
} from 'src/features/resizableWrapper/ResizablePanel';
import { ResizeHandle } from 'src/features/resizableWrapper/ResizeHandle';
import { useContextBar } from 'src/hooks/useContextBar';
import { useMediaQuery } from 'src/hooks/useMediaQuery';
import { useAppSelector } from 'src/hooks/useStore';
import { useWindowSize } from 'src/hooks/useWindowResize';
import { selectCurrentUser } from 'src/store/storeUtils';

type NavTabType = NavTab<DrawerId>;

export const ContextBarLayout = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation();
  const currentUser = useAppSelector(selectCurrentUser);
  const hasAssistantTab = !currentUser?.isClientAccessLimited;

  const { width: viewPortWidth } = useWindowSize();
  const isMobileScreen = useMediaQuery('xs', 'max');
  const {
    open,
    setOpen,
    getActiveDrawer,
    setLastOpenDrawer,
    target,
    isMobileLayout,
    drawerVisibilityConfig,
  } = useContextBar();
  const { entityType, id: entityId } = target ?? {};

  const entityTerm =
    entityType === 'company'
      ? t(($) => $.company, { count: 1 })
      : t(($) => $.client, { count: 1 });

  const navTabs = useMemo<NavTabType[]>(() => {
    const labelMap = isMobileScreen ? MOBILE_DRAWER_LABELS : DRAWER_LABELS;
    const tabs: NavTabType[] = [];

    if (drawerVisibilityConfig.DETAILS) {
      tabs.push({
        key: DRAWER_IDS.DETAILS,
        label: isMobileScreen
          ? labelMap[DRAWER_IDS.DETAILS]
          : `${entityTerm} Details`,
        icon: entityType === 'company' ? 'Building' : 'Profile',
      });
    }

    if (drawerVisibilityConfig.NOTES) {
      tabs.push({
        key: DRAWER_IDS.NOTES,
        label: labelMap[DRAWER_IDS.NOTES],
        icon: 'Note',
      });
    }

    if (hasAssistantTab && drawerVisibilityConfig.INTERNAL_CHAT) {
      tabs.push({
        key: DRAWER_IDS.INTERNAL_CHAT,
        label: labelMap[DRAWER_IDS.INTERNAL_CHAT],
        icon: 'Comment',
      });
    }

    return tabs;
  }, [
    isMobileScreen,
    hasAssistantTab,
    entityType,
    entityTerm,
    drawerVisibilityConfig.DETAILS,
    drawerVisibilityConfig.NOTES,
    drawerVisibilityConfig.INTERNAL_CHAT,
  ]);

  const activeTabKey = getActiveDrawer(hasAssistantTab);

  const handleTabChange = (drawerId: DrawerId) => setLastOpenDrawer(drawerId);

  const entity = useEntity(entityType, entityId);
  const entityName = getEntityName(entity);

  return (
    <AssistantProvider>
      <NoteModalProvider>
        {isMobileLayout ? (
          <>
            <div className="flex flex-col h-full w-full">{children}</div>
            <ImmersiveModalWrapper isOpen={open}>
              <div className="h-[52px] pl-4 pr-3 flex items-center justify-between gap-2 grow-0 bg-gray-100">
                <div className="flex items-center gap-0.5 min-w-0">
                  <IconButton
                    icon="ArrowLeft"
                    size="sm"
                    variant="minimal"
                    onClick={() => setOpen(false)}
                  />
                  <Heading className="truncate">{entityName}</Heading>
                </div>
                {entityType && entityId && (
                  <ContextBarAvatar
                    className="my-4"
                    entityType={entityType}
                    entityId={entityId}
                  />
                )}
              </div>
              <div className="grow-0">
                <ResponsiveTabs<DrawerId>
                  overflow="scroll"
                  tabs={navTabs as Array<Omit<NavTabType, 'icon'>>}
                  activeTabKey={activeTabKey}
                  onTabChange={handleTabChange}
                  className="h-11 border border-solid border-l-0 border-r-0 border-non-hover-border box-border bg-gray-100"
                />
              </div>
              <div className="flex-1 min-h-0">
                <ContextBar />
              </div>
            </ImmersiveModalWrapper>
          </>
        ) : (
          <ResizableGroup widthPersistKey="context-bar">
            <ResizablePanel
              id="app-content"
              order={1}
              className="flex flex-col"
            >
              {children}
            </ResizablePanel>
            {open && (
              <>
                <ResizeHandle />
                <ResizablePanel
                  id="context-bar-content"
                  order={2}
                  {...getRightPanelSizeProps(viewPortWidth, isMobileScreen)}
                >
                  <ContextBar />
                </ResizablePanel>
              </>
            )}
            <div className="w-11 h-full flex flex-col items-center shrink-0 border-0 border-l border-solid border-non-hover-border bg-gray-100">
              {entityType && entityId && (
                <ContextBarAvatar
                  className="my-4"
                  entityType={entityType}
                  entityId={entityId}
                />
              )}
              <ContextBarNavTabs<DrawerId>
                className="py-1.5"
                tabs={navTabs}
                activeTabKey={activeTabKey}
                onTabChange={handleTabChange}
              />
            </div>
          </ResizableGroup>
        )}
      </NoteModalProvider>
    </AssistantProvider>
  );
};
